import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

// Girls collection images
const girlsImages = [
  "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/csyy0sx7_5lXg4mAqh8dTrzx2_SeBC.avif",
  "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/1npsf2rv_MqJmU9qsOh9IgU0D2t-HZ.avif",
  "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/ii1kf2mq_rAkcbLqeGZY-5yB6psUA_.avif",
];

const products = [
  {
    title: "Boys' Collection",
    description: "Shirts, shorts, trousers, and outerwear for ages 2-14 years. Smart casuals and everyday wear in premium fabrics.",
    image: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/kt9t8rmk_PQDlWUkvzfec6enSMF2tU.avif",
    size: "medium",
    items: ["Shirts", "Polo Shirts", "Shorts", "Trousers", "Dungarees"],
  },
  {
    title: "Babies",
    description: "Complete range for 3m-36m including rompers, dresses, and sets. Soft, comfortable, and safe for delicate skin.",
    image: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/1t0x6qz1_OhqlaE5Zf55mt9k76vRUw.avif",
    size: "medium",
    items: ["Rompers", "Dresses", "Sets", "Bodysuits"],
  },
  {
    title: "Ladieswear",
    description: "Blouses, dresses, and tops for women. Supplying to NEXT, Maison 123, and other leading brands.",
    image: "https://images.unsplash.com/photo-1746730921745-5f6afa4c56c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHx3b21hbiUyMHdoaXRlJTIwY290dG9uJTIwZHJlc3MlMjBzdW1tZXIlMjBmYXNoaW9uJTIwbW9kZWx8ZW58MHx8fHwxNzcxODM5NjY5fDA&ixlib=rb-4.1.0&q=85",
    size: "large",
    items: ["Blouses", "Dresses", "Tops", "Skirts"],
  },
];

const fabrics = [
  { name: "Cotton & Cotton Blends", icon: "🌿" },
  { name: "Polyester & Blends", icon: "✨" },
  { name: "Linen & Linen Blends", icon: "🌾" },
  { name: "Viscose & Tencel", icon: "🍃" },
  { name: "Denim", icon: "👖" },
  { name: "Corduroy", icon: "🧵" },
];

const ProductsSection = () => {
  return (
    <section 
      id="products"
      data-testid="products-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <p className="font-sans text-sm font-medium tracking-[0.3em] uppercase text-slate-400 mb-4">
            Our Products
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-slate-900 mb-6">
            Complete Kidswear &<br />
            <span className="text-rose-700">Ladieswear Range</span>
          </h2>
          <p className="font-sans text-lg text-slate-600 max-w-2xl mx-auto">
            From basic casualwear to constructed garments, we manufacture the complete range 
            of children's and ladies' apparel for global fashion brands.
          </p>
        </motion.div>

        {/* Girls Collection - Gallery Style */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-6"
          data-testid="product-card-girls"
        >
          <div className="grid grid-cols-3 gap-4 h-[450px]">
            {girlsImages.map((img, idx) => (
              <div key={idx} className="relative group overflow-hidden bg-white">
                <img
                  src={img}
                  alt={`Girls' Collection ${idx + 1}`}
                  className="w-full h-full object-contain bg-slate-50"
                />
                {idx === 0 && (
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent flex flex-col justify-end p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-serif text-2xl text-white">Girls' Collection</h3>
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-rose-700 transition-colors duration-300">
                        <ArrowUpRight size={20} className="text-white" strokeWidth={1.5} />
                      </div>
                    </div>
                    <p className="font-sans text-sm text-white/80 mb-3">
                      Dresses, blouses, skirts, shorts for ages 2-14 years
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {["Dresses", "Blouses", "Skirts"].map((item, i) => (
                        <span key={i} className="px-2 py-1 bg-white/10 backdrop-blur-sm text-white text-xs font-sans uppercase tracking-wider">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Other Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[400px]">
          {products.map((product, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className={`bento-item relative group cursor-pointer ${
                product.size === 'large' ? 'md:col-span-2 lg:col-span-2' : ''
              }`}
              data-testid={`product-card-${index}`}
            >
              <img
                src={product.image}
                alt={product.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              
              {/* Content Overlay */}
              <div className="absolute inset-0 z-10 p-8 flex flex-col justify-end">
                {/* Title & Arrow */}
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-serif text-2xl md:text-3xl text-white">
                    {product.title}
                  </h3>
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-rose-700 transition-colors duration-300">
                    <ArrowUpRight size={20} className="text-white" strokeWidth={1.5} />
                  </div>
                </div>
                
                {/* Description */}
                <p className="font-sans text-sm text-white/80 mb-4 max-w-md">
                  {product.description}
                </p>
                
                {/* Items Tags */}
                <div className="flex flex-wrap gap-2">
                  {product.items.map((item, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-white/10 backdrop-blur-sm text-white text-xs font-sans uppercase tracking-wider"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Fabrics Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 p-8 md:p-12 bg-white border border-slate-100"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="md:w-1/3">
              <p className="font-sans text-xs font-medium tracking-[0.2em] uppercase text-slate-400 mb-2">
                Fabrics & Fibres
              </p>
              <h3 className="font-serif text-2xl text-slate-900">
                Premium Materials
              </h3>
              <p className="font-sans text-sm text-slate-600 mt-2">
                Sourced from mills across India and imported from China.
              </p>
            </div>
            <div className="md:w-2/3 grid grid-cols-2 md:grid-cols-3 gap-4">
              {fabrics.map((fabric, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <span className="text-2xl">{fabric.icon}</span>
                  <span className="font-sans text-sm text-slate-700">{fabric.name}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;
